'use client'

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NavigateOptions as NextNavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import {
  usePathname as useNextPathname,
  // eslint-disable-next-line no-restricted-imports
  useRouter as useNextRouter,
} from 'next/navigation'
import type { PropsWithChildren } from 'react'
import { createContext, use, useCallback, useEffect, useMemo, useRef } from 'react'
import { useUserInputModifiers } from '../device/useUserInputModifiers.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { isSSR } from '../next/services/nextjs.rendering'
import { ProgressBar } from '@/ui/progress-bar/ProgressBar'
import { isHTTP } from '../utils/link-utils'

// #region Types

type RouterContextApi = {
  // Public api

  /**
   * Refresh the current page with Next app router
   */
  refresh: () => void

  /**
   * Navigate to a new page without progress bar.
   */
  push: (href: string, options?: NextNavigateOptions) => void

  /**
   * - Returns **true** if path *matches the browser's url*
   * - Returns **false** if path *does NOT match the browser's url*
   */
  shouldStartProgressBar: (path: string) => boolean

  /**
   * Navigate to a new page with progress bar.
   */
  pushWithProgressBar: (url: string, options?: NextNavigateOptions) => void

  /**
   * Replace the current page with no progress bar.
   */
  replace: (url: string, options?: NextNavigateOptions) => void

  /**
   * Replace the current page with progress bar.
   */
  replaceWithProgressBar: (url: string, options?: NextNavigateOptions) => void

  /**
   * Go back to the previous page with no progress bar.
   */
  back: () => void

  /**
   * Go back to the previous page with progress bar.
   */
  backWithProgressBar: () => void
  // Internal api

  /** # ☠️ Internal
   * Use at your own risk!
   */
  __internal_start__: () => void

  /** # ☠️ Internal
   * Use at your own risk!
   */
  __internal_complete__: () => void
}

const RouterContext = createContext<RouterContextApi | null>(null)

// #region Constants

const DURATION_TIMEOUT = 40_000

const ID_PROGRESS_TRACK = 'page-navigation-track'

// #region Public API

/**
 * Hook to access the API of our recommended router.
 *
 * Available methods:
 *
 * - `push(url, options)` goes to the new page with no progress bar.
 * - `pushWithProgressBar(url, options)` goes to the new page with progress bar.
 * - `replace(url, options)` replaces the current page with no progress bar.
 * - `replaceWithProgressBar(url, options)` replaces the current page with progress bar.
 * - `refresh()` refreshes the current page with no progress bar.
 * - `backWithProgressBar()` goes back to the previous page with progress bar.
 * - `back()` goes back to the previous page with no progress bar.
 */
export function useRouter(): RouterContextApi {
  return use(RouterContext)!
}

//#region - Other components

/**
 * This component tracks route changes and complete the progress bar.
 *
 * Needs to be in a separate component to avoid re-rendering the whole app
 * and also to access the context.
 * It is not meant to be used outside the app root layout.
 */
export function PageNavigationWatchRouteLoad() {
  const { __internal_complete__ } = useRouter()
  const pathname = useNextPathname()
  const navigationTypeRef = useRef<'popstate' | null>(null)

  /**
   * Track popstate inside the watcher to avoid scroll reset on back/forward
   */
  useEffect(() => {
    if (isSSR) {
      return
    }

    const handlePopstate = () => {
      navigationTypeRef.current = 'popstate'
    }

    window.addEventListener('popstate', handlePopstate)

    return () => {
      window.removeEventListener('popstate', handlePopstate)
    }
  }, [])

  /**
   * On route change, complete progress and scroll to top on arrival when allowed
   */
  useEffect(() => {
    __internal_complete__()

    if (isSSR) {
      return
    }

    const hasAnchor = window.location.hash.length > 1
    // Skip scroll-to-top on browser back/forward to preserve previous scroll position
    const isGoingBackOrForward = navigationTypeRef.current === 'popstate'

    navigationTypeRef.current = null

    if (hasAnchor || isGoingBackOrForward) {
      return
    }

    // Force scroll immediately to fix offset that occurs before this effect runs
    window.scrollTo({ top: 0, left: 0 })
  }, [pathname, __internal_complete__])

  return null
}

/**
 * Contains the markup for the progress bar.
 *
 * Needs to be in a separate component to be rendered at SSR phase so the
 * bar is visible as soon as the page is loaded.
 *
 * It is not meant to be used outside the app root layout.
 */
export function PageNavigationLoaderMarkup() {
  const { t } = useTranslation(['PageNavigation'])

  return (
    <ProgressBar
      id={ID_PROGRESS_TRACK}
      value={0}
      aria-label={t('PageNavigation:loadingPageProgress')}
      className="fixed left-0 top-0 z-page-loader -translate-y-full"
    />
  )
}

type PageNavigationProviderProps = PropsWithChildren & {
  progressBarDisabled?: boolean
}

// #region Provider

/**
 * Provides the page navigation API to the whole app.
 *
 * It is not meant to be used outside the app root layout.
 */
export function PageNavigationProvider({
  children,
  progressBarDisabled = false,
}: PageNavigationProviderProps) {
  // #region - Helpers

  /**
   * Helper function to apply styles to the navigation progress bar and track elements
   * and avoid dealing with null values all along.
   *
   * @example
   * ```ts
   * apply(({ track, bar }) => {
   *   bar({ backgroundColor: 'blue' })
   *   track({ backgroundColor: 'red' })
   * })
   */
  function apply(
    callback: (elements: {
      track(css: Partial<CSSStyleDeclaration>): void
      bar(css: Partial<CSSStyleDeclaration>): void
    }) => void,
  ) {
    if (isSSR) {
      return null
    }

    const track = window.document.querySelector<HTMLElement>(`#${ID_PROGRESS_TRACK}`)
    const bar = track?.querySelector<HTMLElement>('[data-slot="indicator"]')

    if (!track || !bar) {
      console.warn('Navigation progress bar elements not found')
    } else {
      callback({
        track: (css) => Object.assign(track.style, css),
        bar: (css) => Object.assign(bar.style, css),
      })
    }
  }

  // #region - Timeout fallback

  const pageLoadTimedOutToken = useRef<NodeJS.Timeout | null>(null)

  /**
   * Cancel any pending timeout complete animation if the page did load before 30s.
   */
  const cancelTimeoutFallback = useCallback(() => {
    if (pageLoadTimedOutToken.current) {
      clearTimeout(pageLoadTimedOutToken.current)
      pageLoadTimedOutToken.current = null
    }
  }, [])

  // #region - Anim. complete

  const __internal_complete__ = useCallback(() => {
    /**
     * 1. Cancel any pending page timeout to avoid the progress bar being re-completed later.
     */
    cancelTimeoutFallback()

    apply(({ track, bar }) => {
      /**
       * 2. Quickly set the bar width to 100% to make it look like it completed.
       */
      bar({
        transitionDuration: '150ms',
        transitionTimingFunction: 'ease-out',
        transitionProperty: 'width',
        width: '100%',
      })

      /**
       * 3. After a delay, slide the track up to hide it.
       */
      track({
        transitionDelay: '500ms',
        transitionDuration: '300ms',
        transitionTimingFunction: 'linear',
        transitionProperty: 'transform',
        transform: 'translateY(-100%)',
      })
    })
  }, [cancelTimeoutFallback])

  // #region - Anim. start

  const __internal_start__ = useCallback(() => {
    /**
     * 1. Cancel any pending page timeout to avoid the progress bar being re-completed later.
     */
    cancelTimeoutFallback()

    /**
     * 2. Set a new 30s timeout to complete the progress bar if the page load takes too long.
     */
    pageLoadTimedOutToken.current = setTimeout(() => {
      __internal_complete__()
    }, DURATION_TIMEOUT)

    apply(({ bar, track }) => {
      /**
       * 3. Reset the progress bar to 0% width immediately.
       */
      bar({
        transition: 'none',
        width: '0%',
      })

      /**
       * 4. Immediately slide the track down to make it visible.
       */
      track({
        transitionDelay: '',
        transitionDuration: '100ms',
        transitionTimingFunction: 'linear',
        transitionProperty: 'transform',
        transform: 'translateY(0%)',
      })

      /**
       * 5. Animate bar up to width to 90% a bit after step 4 completed.
       */
      requestAnimationFrame(() =>
        // Necessary to ensure the transition is applied correctly.
        bar({
          transitionDelay: '150ms', // Waits for track's slide-down to have finished.
          transitionDuration: `${DURATION_TIMEOUT}ms`, // Max duration before page times out.
          transitionTimingFunction: 'cubic-bezier(0.125, 0.75, 0.25, 1)',
          transitionProperty: 'width',
          width: '90%', // Remaining 10% are to animate faster when the page finished loading.
        }),
      )
    })
  }, [__internal_complete__, cancelTimeoutFallback])

  const { push, replace, refresh, back } = useNextRouter()

  const { alt, meta, ctrl, shift, rightMouse } = useUserInputModifiers().current

  /**
   * For when a user clicks on a link or triggers a navigation.
   * We should start the progress bar only if:
   * - pathname of href and browser location are different
   * - no modifier keys are pressed
   * - right mouse button is not pressed
   * - the link is not a tel: link
   */
  const shouldStartProgressBar = useCallback(
    (href: string) => {
      if (isSSR || progressBarDisabled) {
        return false
      }

      const url = new URL(href, window.location.href)

      const pathIsDifferent =
        url.pathname + url.search !== window.location.pathname + window.location.search

      const isHTTPLink = isHTTP(url)

      const noModifierKeysPressed = !alt && !ctrl && !meta && !shift

      return pathIsDifferent && noModifierKeysPressed && !rightMouse && isHTTPLink
    },
    [alt, ctrl, meta, rightMouse, shift, progressBarDisabled],
  )

  const pushWithProgressBar = useCallback(
    (url: string, options?: NextNavigateOptions) => {
      if (shouldStartProgressBar(url)) {
        // Start the progress bar when the page is pushed.
        __internal_start__()
      }

      push(url, options)
    },
    [__internal_start__, push, shouldStartProgressBar],
  )

  const replaceWithProgressBar = useCallback(
    (url: string, options?: NextNavigateOptions) => {
      if (shouldStartProgressBar(url)) {
        // Start the progress bar when the page is pushed.
        __internal_start__()
      }

      replace(url, options)
    },
    [__internal_start__, replace, shouldStartProgressBar],
  )

  const backWithProgressBar = useCallback(() => {
    // Start the progress bar when the page is pushed.
    __internal_start__()
    back()
  }, [__internal_start__, back])

  const pathname = useNextPathname()
  const navigationTypeRef = useRef<'popstate'>(null)

  // #region - Back/forward nav

  /**
   * Track browser back/forward: tag popstate and start the progress bar.
   */
  useEffect(() => {
    const onBrowserBackForwardButtonClicked = () => {
      navigationTypeRef.current = 'popstate'
      __internal_start__()
    }

    window.addEventListener('popstate', onBrowserBackForwardButtonClicked)

    return () => {
      window.removeEventListener('popstate', onBrowserBackForwardButtonClicked)
    }
  }, [__internal_start__])

  /**
   * Complete the progress bar on popstate route changes and reset state.
   */
  useEffect(() => {
    if (navigationTypeRef.current === 'popstate') {
      // Route changed due to browser back/forward.
      __internal_complete__()
    }

    // Reset for next navigation.
    navigationTypeRef.current = null
  }, [pathname, __internal_complete__])

  return (
    <RouterContext
      value={useMemo(
        () => ({
          __internal_start__,
          __internal_complete__,
          shouldStartProgressBar,
          pushWithProgressBar,
          replaceWithProgressBar,
          backWithProgressBar,
          refresh,
          push,
          replace,
          back,
        }),
        [
          __internal_start__,
          __internal_complete__,
          shouldStartProgressBar,
          pushWithProgressBar,
          replaceWithProgressBar,
          backWithProgressBar,
          refresh,
          push,
          replace,
          back,
        ],
      )}
    >
      {!progressBarDisabled && (
        <>
          <PageNavigationLoaderMarkup />
          <PageNavigationWatchRouteLoad />
        </>
      )}
      {children}
    </RouterContext>
  )
}
